import React, { useMemo, useState } from "react";
import { Table, Button, Checkbox, Dropdown, Space, Divider } from "antd";
import type { TableProps } from "antd";
import type { ColumnType } from "antd/es/table";
import { SettingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

/**
 * A column of a ManageableTable. Requires a stable string `key` (used to persist
 * visibility/width). `hideable: false` pins a column so it can't be hidden
 * (e.g. an actions column). `defaultHidden` hides it until the user opts in.
 */
export type ManageableColumn<T> = ColumnType<T> & {
  key: string;
  hideable?: boolean;
  defaultHidden?: boolean;
};

type Props<T> = Omit<TableProps<T>, "columns"> & {
  /** Stable id — user column prefs are persisted per tableId. */
  tableId: string;
  columns: ManageableColumn<T>[];
  /** Extra toolbar content rendered to the left of the Columns button. */
  toolbarExtra?: React.ReactNode;
  minColumnWidth?: number;
};

type Prefs = {
  hidden: string[];
  widths: Record<string, number>;
};

const PREFS_PREFIX = "table-prefs:";

function loadPrefs(tableId: string): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_PREFIX + tableId);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        hidden: Array.isArray(parsed.hidden) ? parsed.hidden : [],
        widths:
          parsed.widths && typeof parsed.widths === "object"
            ? parsed.widths
            : {},
      };
    }
  } catch {
    /* ignore corrupt prefs */
  }
  return { hidden: [], widths: {} };
}

function savePrefs(tableId: string, prefs: Prefs) {
  try {
    localStorage.setItem(PREFS_PREFIX + tableId, JSON.stringify(prefs));
  } catch {
    /* ignore quota errors */
  }
}

/**
 * Header cell with a drag handle on its right edge. Dependency-free — uses
 * native pointer events, so no react-resizable is needed.
 */
const ResizableHeaderCell: React.FC<any> = (props) => {
  const { onResize, width, minWidth, ...rest } = props;

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = width as number;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    const onMove = (moveEvent: MouseEvent) => {
      const next = Math.max(
        minWidth ?? 60,
        startWidth + (moveEvent.clientX - startX)
      );
      onResize(next);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  return (
    <th {...rest} style={{ ...rest.style, position: "relative" }}>
      {rest.children}
      {width ? (
        <span
          onMouseDown={startResize}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            right: -4,
            top: 0,
            height: "100%",
            width: 8,
            cursor: "col-resize",
            zIndex: 1,
            userSelect: "none",
            touchAction: "none",
          }}
        />
      ) : null}
    </th>
  );
};

function ManageableTable<T extends object>({
  tableId,
  columns,
  toolbarExtra,
  minColumnWidth = 80,
  scroll,
  ...tableProps
}: Props<T>) {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState<Prefs>(() => {
    const loaded = loadPrefs(tableId);
    // Seed default-hidden columns the first time (no saved prefs yet).
    if (!localStorage.getItem(PREFS_PREFIX + tableId)) {
      loaded.hidden = columns
        .filter((c) => c.defaultHidden)
        .map((c) => c.key);
    }
    return loaded;
  });

  const update = (next: Prefs) => {
    setPrefs(next);
    savePrefs(tableId, next);
  };

  const toggleColumn = (key: string, visible: boolean) => {
    const hidden = visible
      ? prefs.hidden.filter((k) => k !== key)
      : [...new Set([...prefs.hidden, key])];
    update({ ...prefs, hidden });
  };

  const resizeColumn = (key: string) => (width: number) => {
    update({ ...prefs, widths: { ...prefs.widths, [key]: width } });
  };

  const resetPrefs = () => {
    localStorage.removeItem(PREFS_PREFIX + tableId);
    update({
      hidden: columns.filter((c) => c.defaultHidden).map((c) => c.key),
      widths: {},
    });
  };

  const hiddenSet = useMemo(() => new Set(prefs.hidden), [prefs.hidden]);

  const visibleColumns = useMemo(() => {
    return columns
      .filter((c) => !hiddenSet.has(c.key))
      .map((col) => {
        const width = prefs.widths[col.key] ?? col.width;
        return {
          ...col,
          width,
          onHeaderCell: () =>
            ({
              width: typeof width === "number" ? width : undefined,
              minWidth: minColumnWidth,
              onResize: resizeColumn(col.key),
            } as any),
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, hiddenSet, prefs.widths]);

  const hideableColumns = columns.filter((c) => c.hideable !== false);

  const columnsMenu = (
    <div
      style={{
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 6px 16px rgba(0,0,0,.12)",
        padding: "8px 12px",
        maxHeight: 320,
        overflowY: "auto",
        minWidth: 200,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        {t("Show columns")}
      </div>
      <Space direction="vertical" size={4}>
        {hideableColumns.map((col) => (
          <Checkbox
            key={col.key}
            checked={!hiddenSet.has(col.key)}
            onChange={(e) => toggleColumn(col.key, e.target.checked)}
          >
            {typeof col.title === "string" ? col.title : col.key}
          </Checkbox>
        ))}
      </Space>
      <Divider style={{ margin: "8px 0" }} />
      <Button type="link" size="small" style={{ padding: 0 }} onClick={resetPrefs}>
        {t("Reset")}
      </Button>
    </div>
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        {toolbarExtra}
        <Dropdown
          trigger={["click"]}
          dropdownRender={() => columnsMenu}
          placement="bottomRight"
        >
          <Button icon={<SettingOutlined />}>{t("Columns")}</Button>
        </Dropdown>
      </div>
      <Table<T>
        {...tableProps}
        columns={visibleColumns}
        components={{ header: { cell: ResizableHeaderCell } }}
        scroll={{ x: "max-content", ...(scroll || {}) }}
      />
    </div>
  );
}

export default ManageableTable;
